/**
 * HumanInputBox Component (SWRM-111)
 * 
 * Chat-style input box for providing human feedback to paused workflows.
 * Displayed when a workflow is paused and waiting for input.
 */

import React, { useState } from "react";
import { Send, Loader } from "lucide-react";

interface HumanInputBoxProps {
  workflowId: string;
  stepId?: string;
  onSubmit: (content: string) => Promise<void>;
  className?: string;
}

export const HumanInputBox: React.FC<HumanInputBoxProps> = ({
  workflowId,
  stepId,
  onSubmit,
  className = "",
}) => {
  const [content, setContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate input
    if (!content.trim()) {
      setError("Please enter a message");
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      await onSubmit(content.trim());
      setContent(""); // Clear input on success
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send message");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Ctrl+Enter or Cmd+Enter
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  const charCount = content.length;
  const maxChars = 10000;
  const isOverLimit = charCount > maxChars;

  return (
    <div
      className={`bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300 dark:border-amber-600 rounded-lg p-4 ${className}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-amber-400 rounded-full animate-pulse"></div>
          <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100">
            Workflow Paused - Provide Input
          </h3>
        </div>
        <span
          className={`text-xs ${
            isOverLimit
              ? "text-red-600 dark:text-red-400 font-bold"
              : "text-gray-500 dark:text-gray-400"
          }`}
        >
          {charCount} / {maxChars}
        </span>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
        The agent needs your help! Type your feedback, corrections, or instructions below.
      </p>

      {/* Input Form */}
      <form onSubmit={handleSubmit}>
        <div className="space-y-3">
          {/* Textarea */}
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your message here... (Ctrl+Enter to send)"
            disabled={isSubmitting}
            rows={4}
            className={`w-full px-3 py-2 border rounded-md resize-y min-h-[100px] max-h-[300px]
              ${
                isOverLimit
                  ? "border-red-500 focus:ring-red-500"
                  : "border-gray-300 dark:border-gray-600 focus:ring-blue-500"
              }
              bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
              focus:outline-none focus:ring-2 transition-colors
              disabled:opacity-50 disabled:cursor-not-allowed
            `}
          />

          {/* Error Message */}
          {error && (
            <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-md">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Tip: Press Ctrl+Enter to send
            </div>
            <button
              type="submit"
              disabled={isSubmitting || !content.trim() || isOverLimit}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? (
                <>
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Help Text */}
      <div className="mt-3 pt-3 border-t border-amber-200 dark:border-amber-700">
        <p className="text-xs text-gray-600 dark:text-gray-400">
          After sending, the workflow will automatically resume with your input.
          You can also use the regular Resume button to continue without providing input.
        </p>
      </div>
    </div>
  );
};

export default HumanInputBox;


